import { useState } from "react";
import { Link } from "react-router-dom";
import PageLayout from "@/components/feature/PageLayout";
import useSeo from "@/hooks/useSeo";
import { topics, type Topic } from "@/mocks/manga";

const groups = ["全部", "新番安利", "剧情分析", "完结复盘", "求书求助", "站务反馈"];

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "笨狗漫画网社区讨论",
    url: "https://comicsur.github.io/discussion",
    inLanguage: "zh-CN",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://comicsur.github.io/" },
      { "@type": "ListItem", position: 2, name: "社区讨论", item: "https://comicsur.github.io/discussion" },
    ],
  },
];

export default function DiscussionPage() {
  const [group, setGroup] = useState("全部");
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [posted, setPosted] = useState(false);
  const [list, setList] = useState<Topic[]>(topics);

  const filtered = group === "全部" ? list : list.filter((t) => t.group === group);
  const hotSide = [...topics].filter((t) => t.hot).slice(0, 4);

  const handlePost = (e: React.FormEvent) => {
    e.preventDefault();
    if (!title.trim() || !body.trim()) return;
    const newTopic: Topic = {
      id: `u-${Date.now()}`,
      title: title.trim(),
      group: "新番安利",
      replies: 0,
      views: 1,
      hot: true,
      summary: body.trim(),
      author: "热心漫友",
      time: "刚刚",
    };
    setList([newTopic, ...list]);
    setTitle("");
    setBody("");
    setOpen(false);
    setPosted(true);
    setTimeout(() => setPosted(false), 3000);
  };

  useSeo({
    title: "社区讨论 - 漫画话题广场与追更交流 | 笨狗漫画网",
    description:
      "笨狗漫画网社区讨论区，新番安利、剧情分析、完结复盘、求书求助等话题等你来聊，和同好一起讨论热门漫画。",
    jsonLd,
  });

  return (
    <PageLayout>
      <div className="bg-dots">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-10 md:py-14">
          <nav className="flex items-center gap-2 text-sm text-foreground-600" aria-label="面包屑">
            <Link to="/" className="hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line text-foreground-400" aria-hidden="true"></i>
            <span className="text-foreground-950">社区讨论</span>
          </nav>

          <div className="mt-4 flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
            <div className="max-w-2xl">
              <h1 className="font-heading text-3xl text-foreground-950 md:text-4xl">社区讨论广场</h1>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">
                新番安利、剧情分析、完结复盘，和同好一起交流你最爱的漫画，快来说说你的看法。
              </p>
            </div>
            <button
              type="button"
              onClick={() => setOpen(true)}
              className="flex w-max items-center gap-2 whitespace-nowrap rounded-full bg-primary-500 px-6 py-3 text-sm font-bold text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-edit-line" aria-hidden="true"></i> 发布讨论
            </button>
          </div>

          <div className="mt-6 flex w-max max-w-full flex-wrap items-center gap-2 rounded-full bg-background-100 p-1.5">
            {groups.map((g) => (
              <button
                key={g}
                type="button"
                onClick={() => setGroup(g)}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                  group === g ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:bg-background-200"
                }`}
              >
                {g}
              </button>
            ))}
          </div>

          <div className="mt-8 grid grid-cols-1 gap-6 lg:grid-cols-3">
            <div className="lg:col-span-2">
              {posted && (
                <div className="mb-4 flex items-center gap-2 rounded-2xl bg-primary-100 px-4 py-3 text-sm font-medium text-primary-800">
                  <i className="ri-checkbox-circle-fill text-lg" aria-hidden="true"></i>
                  发布成功，你的讨论已出现在列表中
                </div>
              )}
              <div className="space-y-3">
                {filtered.map((topic) => (
                  <article
                    key={topic.id}
                    className="rounded-2xl bg-background-100 p-4 transition-colors hover:bg-background-200"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <h2 className="text-base font-bold text-foreground-950">{topic.title}</h2>
                      {topic.hot && (
                        <span className="flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2 py-0.5 text-xs font-bold text-accent-700">
                          <i className="ri-fire-fill" aria-hidden="true"></i> 热门
                        </span>
                      )}
                    </div>
                    <p className="mt-1 text-sm text-foreground-600">{topic.summary}</p>
                    <div className="mt-3 flex flex-wrap items-center gap-3 text-xs text-foreground-600">
                      <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-secondary-900">{topic.group}</span>
                      <span className="flex items-center gap-1"><i className="ri-chat-3-line" aria-hidden="true"></i>{topic.replies} 回复</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line" aria-hidden="true"></i>{topic.views}</span>
                      <span className="hidden sm:inline">{topic.author} · {topic.time}</span>
                    </div>
                  </article>
                ))}
              </div>
            </div>

            <aside className="space-y-4">
              <div className="rounded-2xl bg-background-100 p-5">
                <h3 className="font-heading text-lg text-foreground-950">热门话题</h3>
                <div className="mt-4 space-y-3">
                  {hotSide.map((t, i) => (
                    <button
                      key={t.id}
                      type="button"
                      className="group flex w-full items-start gap-3 text-left"
                    >
                      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md bg-accent-100 text-xs font-bold text-accent-700">
                        {i + 1}
                      </span>
                      <span className="line-clamp-2 text-sm text-foreground-800 group-hover:text-primary-600">
                        {t.title}
                      </span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="rounded-2xl bg-secondary-500 p-5 text-background-50">
                <h3 className="font-heading text-lg">社区公约</h3>
                <ul className="mt-3 space-y-2 text-xs text-secondary-100">
                  <li className="flex gap-2"><i className="ri-check-line mt-0.5 shrink-0" aria-hidden="true"></i>围绕漫画理性讨论</li>
                  <li className="flex gap-2"><i className="ri-check-line mt-0.5 shrink-0" aria-hidden="true"></i>剧透请标注提醒</li>
                  <li className="flex gap-2"><i className="ri-check-line mt-0.5 shrink-0" aria-hidden="true"></i>友善交流，拒绝攻击</li>
                  <li className="flex gap-2"><i className="ri-check-line mt-0.5 shrink-0" aria-hidden="true"></i>禁广告与刷屏</li>
                </ul>
              </div>
            </aside>
          </div>
        </div>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background-950/60 px-4" onClick={() => setOpen(false)}>
          <div
            className="w-full max-w-lg rounded-2xl bg-background-50 p-6"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="font-heading text-xl text-foreground-950">发布讨论</h2>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="flex h-9 w-9 items-center justify-center rounded-full bg-background-100 text-foreground-700"
                aria-label="关闭"
              >
                <i className="ri-close-line text-lg" aria-hidden="true"></i>
              </button>
            </div>
            <form onSubmit={handlePost} className="mt-5 space-y-4">
              <div>
                <label htmlFor="topic-title" className="text-sm font-medium text-foreground-800">讨论标题</label>
                <input
                  id="topic-title"
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="写一个吸引人的标题"
                  className="mt-1.5 w-full rounded-xl bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none ring-primary-300 focus:ring-2 placeholder:text-foreground-400"
                />
              </div>
              <div>
                <label htmlFor="topic-body" className="text-sm font-medium text-foreground-800">讨论内容</label>
                <textarea
                  id="topic-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="分享你的观点、分析或安利"
                  maxLength={500}
                  rows={4}
                  className="mt-1.5 w-full resize-none rounded-xl bg-background-100 px-4 py-3 text-sm text-foreground-900 outline-none ring-primary-300 focus:ring-2 placeholder:text-foreground-400"
                ></textarea>
              </div>
              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="rounded-full bg-background-100 px-5 py-2.5 text-sm font-medium text-foreground-700 hover:bg-background-200"
                >
                  取消
                </button>
                <button
                  type="submit"
                  className="whitespace-nowrap rounded-full bg-primary-500 px-6 py-2.5 text-sm font-bold text-background-50 hover:bg-primary-600"
                >
                  发布
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </PageLayout>
  );
}